import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AchMethodDetailsModel } from '@data-layer/models/companies';
import { SelfFilerRepository } from '@data-layer/repositories';
import { ACH_FORM_400_LINK } from '@constants/constants';
import { enumFilerCodeState } from '@enum/enumFilerCodeState';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumGATagEventLabel } from '@enum/enumGATagEventLabel';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { SelfFilerService } from '@services/self-filer.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { CompaniesService } from '@services/companies.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';

@Component({
  selector: 'self-filer-ach-debit-form',
  templateUrl: './self-filer-ach-debit-form.component.html',
  styleUrls: ['./self-filer-ach-debit-form.component.scss'],
})
export class SelfFilerAchDebitFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public hasFilerCode: boolean = false;
  @Input() public achMethodDetails: AchMethodDetailsModel;
  @Input() public companyId: number;
  @Input() public companyAuthorizationAgreementAccepted: boolean;
  @Output() public update: EventEmitter<any> = new EventEmitter();

  public isOpenForm: boolean = false;
  public ACH_FORM_400_LINK = ACH_FORM_400_LINK;
  public formGroup: UntypedFormGroup;
  public CUSTOM_MASK = CUSTOM_MASK;
  public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.DEFAULT;
  public menuMode: enumDropdownActionMenuMode = enumDropdownActionMenuMode.LIGHT;
  public errors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    maxlength: 'FORM_ERROR.MAX_LENGTH',
    minlength: 'FORM_ERROR.MIN_LENGTH',
  };
  public showPUNExampleModal: boolean = false;
  public isLoadingCompanyPun: boolean = false;
  public companyDocuments: DocumentModel[] = [];

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private selfFilerService: SelfFilerService,
    private selfFilerRepository: SelfFilerRepository,
    private ga: GoogleAnalyticsService,
    private companiesService: CompaniesService,
    private documentsService: DocumentsService,
  ) {}

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      paymentUnitNumber: new UntypedFormControl(null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
      authorizationAgreementAccepted: new UntypedFormControl(false, Validators.requiredTrue),
    });
    this.formGroup
      .get('paymentUnitNumber')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe(() => this.checkDisabledAgreement());
    this.formGroup.get('paymentUnitNumber').patchValue(this.achMethodDetails?.paymentUnitNumber);

    this.getCompanyDocuments(true).pipe(takeUntil(this.isDestroy)).subscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.achMethodDetails && this.achMethodDetails && !!this.formGroup) {
      this.formGroup.get('paymentUnitNumber').patchValue(this.achMethodDetails.paymentUnitNumber);
    }
    if (changes.companyAuthorizationAgreementAccepted) {
      this.checkDisabledAgreement();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get isSuccess() {
    return this.selfFilerService.getOnBoardingAchMethodState(this.achMethodDetails) === enumFilerCodeState.SUCCESS;
  }

  public clearAndSave() {
    this.selfFilerRepository
      .deletePaymentUnitNumber(this.companyId)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.update.emit();
        this.isOpenForm = false;
        this.formGroup.get('paymentUnitNumber').reset();
      });
  }

  public openForm() {
    this.isOpenForm = true;
  }

  public goToForm() {
    this.selfFilerRepository
      .sendAchMethodRequest(this.companyId)
      .pipe(
        tap(() => {
          this.ga.emitCustomEvent(
            enumGATagEventCategory.OnBoarding,
            enumGATagEventAction.OBAahMethodRequest,
            enumGATagEventLabel.OBAchMethodGoToForm,
          );
        }),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => {
        this.update.emit();
        window.open(ACH_FORM_400_LINK, '_blank');
      });
  }

  public save() {
    const acceptAuthorizationAgreement =
      !this.companyAuthorizationAgreementAccepted && this.formGroup.get('authorizationAgreementAccepted').value;
    this.selfFilerRepository
      .setPaymentUnitNumber(this.formGroup.get('paymentUnitNumber').value, this.companyId)
      .pipe(
        tap(() => {
          this.ga.emitCustomEvent(
            enumGATagEventCategory.OnBoarding,
            enumGATagEventAction.OBAchMethodUpdateInfo,
            `PUN: ${this.formGroup.get('paymentUnitNumber').value}`,
          );
        }),
        switchMap(() => (acceptAuthorizationAgreement ? this.companiesService.acceptAuthorizationAgreement() : of(null))),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => {
        this.update.emit();
        this.isOpenForm = false;
        this.formGroup.get('paymentUnitNumber').reset();
      });
  }

  public openPUNExampleModal(event: Event): void {
    event.preventDefault();
    this.showPUNExampleModal = true;
  }

  public get punDocument(): DocumentModel {
    return this.companyDocuments.find((document) => document.type === enumDocumentType.ACH_APPROVAL);
  }

  public updateDocumentState(documentId: number, state: enumDocumentState): void {
    this.companyDocuments = this.companyDocuments.map((document) => (document.id === documentId ? { ...document, state } : document));
  }

  public uploadDocument(file: File): void {
    this.isLoadingCompanyPun = true;
    this.documentsService
      .uploadDocument(
        {
          fileName: file.name,
          relatedEntityId: this.companyId,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          rootEntityId: this.companyId,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: enumDocumentType.ACH_APPROVAL,
        },
        file,
      )
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => (this.isLoadingCompanyPun = false)),
        takeUntil(this.isDestroy),
      )
      .subscribe();
  }

  public deleteDocument(document: DocumentModel): void {
    this.isLoadingCompanyPun = true;
    this.documentsService
      .deleteDocument(document.id, false)
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => (this.isLoadingCompanyPun = false)),
        takeUntil(this.isDestroy),
      )
      .subscribe();
  }

  private checkDisabledAgreement(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.get('authorizationAgreementAccepted').setValue(this.companyAuthorizationAgreementAccepted);

    if (
      !this.companyAuthorizationAgreementAccepted &&
      this.formGroup.get('paymentUnitNumber').value &&
      this.formGroup.get('paymentUnitNumber').valid
    ) {
      this.formGroup.get('authorizationAgreementAccepted').enable();
    } else {
      this.formGroup.get('authorizationAgreementAccepted').disable();
    }
  }

  private getCompanyDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getCompanyDocuments(this.companyId, isShowLoader).pipe(
      tap((documents) => {
        this.companyDocuments = documents;
      }),
    );
  }
}
